import { useState, useEffect, useContext } from 'react';
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl, remove } from 'aws-amplify/storage';
import { useNavigate } from 'react-router-dom';
import { lang } from "moment-timezone";
import { captureError } from '../../../../utils/sentryHelpers';
import { UserContext } from '../../../../App';
import { isAdminOrModerator } from '../../../../utils/roles';
import { slugMutationInput } from '../../../../utils/slugInput';

export const useEventData = (eventId) => {
  const navigate = useNavigate();
  const client = generateClient();
  const { user } = useContext(UserContext);
  // Public URL slug: only Admin/Moderator see and write it (GeneralTab); everyone else must not send "slug"
  const canEditSlug = isAdminOrModerator(user);
  
  const [event, setEvent] = useState({});
  // Slug as loaded with the event; "slug" is only sent when it was changed here (see slugMutationInput)
  const [loadedSlug, setLoadedSlug] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingImageError, setUploadingImageError] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadingFileError, setUploadingFileError] = useState(null);

  useEffect(() => {
    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  const fetchEvent = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await client.graphql({
        query: GET_EVENT_QUERY,
        variables: { id: eventId },
      });
      setEvent(response.data.getEvent);
      setLoadedSlug(response.data.getEvent?.slug ?? null);
    } catch (error) {
      console.error('Error fetching event:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'fetch-event',
          consoleMessage: 'Error fetching event',
          context: { eventId }
      });
      setLoadError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value, language = null) => {
    setEvent(prev => {
      if (language) {
        const updatedField = prev[field] ? [...prev[field]] : [];
        const index = updatedField.findIndex(item => item.language === language);
        if (index !== -1) {
          updatedField[index] = { ...updatedField[index], text: value };
        } else {
          updatedField.push({ language, text: value });
        }
        return { ...prev, [field]: updatedField };
      } else {
        return { ...prev, [field]: value };
      }
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    try {
      setIsUploadingImage(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        try {
          const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
          const response = await client.graphql({
            query: UPLOAD_IMAGE_MUTATION,
            variables: {
              imageBuffer: base64FileBuffer,
              imageType: "eventImage",
            },
          });
          
          const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
          setEvent(oldData => ({...oldData, image: {fileName}}));
        } catch (error) {
          console.error('Error uploading image:', error);
          captureError(error, {
              feature: 'event-management-admin',
              operation: 'upload-event-image',
              consoleMessage: 'Error uploading image',
              context: {}
          });
          setUploadingImageError(error.errors?.[0].message);
        }
      };

      reader.onerror = error => {
        console.error('Error reading file:', error);
        captureError(error, {
            feature: 'event-management-admin',
            operation: 'read-event-image-file',
            consoleMessage: 'Error reading file',
            context: {}
        });
        setUploadingImageError(error);
      };
    } catch (error) {
      console.error('Error handling image upload:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'handle-event-image-upload',
          consoleMessage: 'Error handling image upload',
          context: {}
      });
      setUploadingImageError(error.errors?.[0].message);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleBadgePreviewUpload = async (file) => {
    if (!file) return;
    
    try {
      setIsUploadingImage(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        try {
          const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
          const response = await client.graphql({
            query: UPLOAD_IMAGE_MUTATION,
            variables: {
              imageBuffer: base64FileBuffer,
              imageType: "eventImage",
            },
          });
          
          const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
          setEvent(oldData => ({...oldData, badgePreview: {fileName}}));
        } catch (error) {
          console.error('Error uploading badge preview:', error);
          captureError(error, {
              feature: 'event-management-admin',
              operation: 'upload-badge-preview',
              consoleMessage: 'Error uploading badge preview',
              context: {}
          });
          setUploadingImageError(error.errors?.[0].message);
        }
      };

      reader.onerror = error => {
        console.error('Error reading file:', error);
        captureError(error, {
            feature: 'event-management-admin',
            operation: 'read-badge-preview-file',
            consoleMessage: 'Error reading file',
            context: {}
        });
        setUploadingImageError(error);
      };
    } catch (error) {
      console.error('Error handling badge preview upload:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'handle-badge-preview-upload',
          consoleMessage: 'Error handling badge preview upload',
          context: {}
      });
      setUploadingImageError(error.errors?.[0].message);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    try {
      setIsUploadingFile(true);
      const fileName = file.name;
      const result = await uploadData({
        key: "eventFiles/" + fileName,
        data: file,
      }).result;

      const fileURL = await getUrl({ key: "public/eventFiles/" + fileName });
      setEvent(oldData => ({
        ...oldData,
        floorPlan: {
          fileName: fileName,
          s3Path: "public/eventFiles/" + fileName
        }
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'upload-event-file',
          consoleMessage: 'Error uploading file',
          context: {}
      });
      setUploadingFileError(error.errors?.[0]?.message || error.message);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleRemoveFile = async (path) => {
    try {
      await remove({ key: path });
      setEvent(oldData => ({...oldData, floorPlan: null}));
    } catch (error) {
      console.error('Error removing file:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'remove-event-file',
          consoleMessage: 'Error removing file',
          context: { path }
      });
    }
  };

  const handleFileDownload = async (path) => {
    try {
      const getUrlResult = await getUrl({
        key: path.replace('public/', ''),
      });
      window.open(getUrlResult.url, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'download-event-file',
          consoleMessage: 'Error downloading file',
          context: { path }
      });
    }
  };

  const handleProgramUpload = async (file) => {
    if (!file) return;

    try {
      setIsUploadingFile(true);
      const fileName = file.name;
      const result = await uploadData({
        key: "eventFiles/" + fileName,
        data: file,
      }).result;

      const fileURL = await getUrl({ key: "public/eventFiles/" + fileName });
      setEvent(oldData => ({
        ...oldData,
        program: {
          fileName: fileName,
          s3Path: "public/eventFiles/" + fileName
        }
      }));
    } catch (error) {
      console.error('Error uploading program file:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'upload-event-program',
          consoleMessage: 'Error uploading program file',
          context: {}
      });
      setUploadingFileError(error.errors?.[0]?.message || error.message);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleProgramRemove = async (path) => {
    try {
      await remove({ key: path });
      setEvent(oldData => ({...oldData, program: null}));
    } catch (error) {
      console.error('Error removing program file:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'remove-event-program',
          consoleMessage: 'Error removing program file',
          context: { path }
      });
    }
  };

  const handleProgramDownload = async (path) => {
    try {
      const getUrlResult = await getUrl({
        key: path.replace('public/', ''),
      });
      window.open(getUrlResult.url, '_blank');
    } catch (error) {
      console.error('Error downloading program file:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'download-event-program',
          consoleMessage: 'Error downloading program file',
          context: { path }
      });
    }
  };

  const handleHighlightVideoUpload = async (file) => {
    if (!file) return null;
    try {
      setIsUploadingFile(true);
      const fileName = `${Date.now()}-${file.name}`;
      const key = `eventHighlightVideos/${fileName}`;
      await uploadData({
        key,
        data: file,
        options: { accessLevel: 'guest' },
      }).result;
      return { fileName, s3Path: `public/${key}` };
    } catch (error) {
      console.error('Error uploading highlight video:', error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'upload-highlight-video',
        consoleMessage: 'Error uploading highlight video',
        context: { fileName: file?.name }
      });
      setUploadingFileError(error.errors?.[0]?.message || error.message);
      return null;
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Press media are downloaded 1:1 from the website, so the original file name must survive:
  // it is kept as the last S3 key segment (a timestamp folder avoids collisions between uploads
  // with the same name) and additionally pinned via Content-Disposition for downloads.
  const handlePressMediaUpload = async (file) => {
    if (!file) return null;
    try {
      setIsUploadingFile(true);
      const key = `eventPressMedia/${Date.now()}/${file.name}`;
      await uploadData({
        key,
        data: file,
        options: {
          accessLevel: 'guest',
          contentType: file.type || 'application/octet-stream',
          contentDisposition: `attachment; filename*=UTF-8''${encodeURIComponent(file.name)}`,
        },
      }).result;
      return { fileName: file.name, s3Path: `public/${key}` };
    } catch (error) {
      console.error('Error uploading press media file:', error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'upload-press-media',
        consoleMessage: 'Error uploading press media file',
        context: { fileName: file?.name }
      });
      setUploadingFileError(error.errors?.[0]?.message || error.message);
      return null;
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleHighlightImageUpload = async (file) => {
    if (!file) return null;
    return new Promise((resolve) => {
      try {
        setIsUploadingImage(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = async () => {
          try {
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: UPLOAD_IMAGE_MUTATION,
              variables: {
                imageBuffer: base64FileBuffer,
                imageType: "eventImage",
              },
            });
            const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
            resolve({ fileName });
          } catch (error) {
            console.error('Error uploading highlight image:', error);
            captureError(error, {
              feature: 'event-management-admin',
              operation: 'upload-highlight-image',
              consoleMessage: 'Error uploading highlight image',
              context: { fileName: file?.name }
            });
            setUploadingImageError(error.errors?.[0]?.message || error.message);
            resolve(null);
          } finally {
            setIsUploadingImage(false);
          }
        };

        reader.onerror = (error) => {
          console.error('Error reading highlight image file:', error);
          captureError(error, {
            feature: 'event-management-admin',
            operation: 'read-highlight-image-file',
            consoleMessage: 'Error reading highlight image file',
            context: {}
          });
          setUploadingImageError(error);
          setIsUploadingImage(false);
          resolve(null);
        };
      } catch (error) {
        console.error('Error handling highlight image upload:', error);
        setIsUploadingImage(false);
        resolve(null);
      }
    });
  };

  const handleProductData = async (eventData) => {
    // Build Product Data
    let productData = {
      type: "ticket",
      subType: "eventTicket",
      name: eventData.name.map(n => ({
        language: n.language,
        text: "Ticket " + n.text
      })
      ),
      sku: `TICKET-${event.name.find(n => n.language === "en")?.text.replace(/ /g, '-').toUpperCase() || event.name[0]?.text.replace(/ /g, '-').toUpperCase()}`,
      
      image: {
        main: {
          fileName: eventData.image?.fileName || null
        }
      },
      allowedBuyerGroups: ["business", "consumer"],
      requiredShipping: false,
      icon: "ticket-alt",
    };

    if (eventData.ticketProduct) {
      // Check if the product has been changed on the fields: name, price, image
      if(eventData.ticketProduct.name?.[0]?.text === productData.name?.[0]?.text &&
        eventData.ticketProduct.price === productData.price &&
        eventData.ticketProduct?.image?.main?.fileName === productData?.image?.main?.fileName) {
          return eventData.ticketProduct.id;
      }


        await client.graphql({
            query: /* GraphQL */ `
            mutation UpdateProduct($input: UpdateProductInput!) {
                updateProduct(input: $input) {
                    id
                }
            }
            `,
            variables: {
                input: {
                    id: eventData.ticketProduct.id,
                    ...productData
                }
            }
        });
        return eventData.ticketProduct.id;
    } else {
        const { data } = await client.graphql({
            query: /* GraphQL */ `
            mutation CreateProduct($input: CreateProductInput!) {
                createProduct(input: $input) {
                    id
                }
            }
            `,
            variables: {
                input: productData
            }
        });
        return data.createProduct.id;
    }
};

  const updateEvent = async () => {
    try {
      let eventTicketProductId = await handleProductData(event);
      let updatedEvent = { ...event, eventTicketProductId };
      delete updatedEvent.industries;
      delete updatedEvent.ticketProduct;

      if (Array.isArray(updatedEvent.daySchedules)) {
        updatedEvent.daySchedules = updatedEvent.daySchedules.map(s => ({
          day: s?.day ?? null,
          date: s?.date || null,
          exhibitorStart: s?.exhibitorStart || null,
          exhibitorEnd: s?.exhibitorEnd || null,
          visitorStart: s?.visitorStart || null,
          visitorEnd: s?.visitorEnd || null,
          note: Array.isArray(s?.note)
            ? s.note.map(n => ({ language: n?.language, text: n?.text || '' }))
            : null,
        }));
      }

      // Convert empty strings to null (GraphQL AWSDate etc. don't accept '')
      Object.keys(updatedEvent).forEach(key => {
        if (updatedEvent[key] === '') updatedEvent[key] = null;
      });

      // slugHistory is read-only; "slug" is Admin/Moderator only (AppSync field auth rejects the
      // whole mutation otherwise) and is only sent when it was changed vs. the loaded slug:
      // on create an empty slug is omitted (generated server-side), on update an unchanged slug is
      // omitted and clearing a previously set slug sends null so the backend regenerates it.
      delete updatedEvent.slugHistory;
      delete updatedEvent.slug;
      const slugInput = slugMutationInput(event.slug, { canEditSlug, isUpdate: !!eventId, loadedSlug });
      Object.assign(updatedEvent, slugInput);

      if (eventId) {
        await client.graphql({
          query: UPDATE_EVENT_MUTATION,
          variables: { input: { id: eventId, ...updatedEvent } },
        });
        // The page stays open after saving: remember what was sent so later saves omit an unchanged slug
        if ('slug' in slugInput) setLoadedSlug(slugInput.slug);
      } else {
        const result = await client.graphql({
          query: CREATE_EVENT_MUTATION,
          variables: { input: updatedEvent },
        });
        // Return the new ID if this was a creation
        setEvent(oldData => ({...oldData, id: result.data.createEvent.id, ticketProduct: {id: eventTicketProductId}}));
        return result.data.createEvent.id;
      }
    } catch (error) {
      console.error('Error saving event:', error);
      captureError(error, {
          feature: 'event-management-admin',
          operation: 'save-event',
          consoleMessage: 'Error saving event',
          context: { eventId }
      });
      throw error; // Propagate the error to handle it in the component
    }
  };

  return {
    event,
    setEvent,
    isLoading,
    loadError,
    isUploadingImage,
    uploadingImageError,
    isUploadingFile,
    uploadingFileError,
    handleInputChange,
    handleImageUpload,
    handleBadgePreviewUpload,
    handleFileUpload,
    handleRemoveFile,
    handleFileDownload,
    handleProgramUpload,
    handleProgramRemove,
    handleProgramDownload,
    handleHighlightVideoUpload,
    handleHighlightImageUpload,
    handlePressMediaUpload,
    updateEvent
  };
};

// GraphQL Queries and Mutations
const GET_EVENT_QUERY = /* GraphQL */ `
  query GetEvent($id: ID!) {
    getEvent(id: $id) {
      id
      type
      name {
        language
        text
      }
      slug
      slugHistory
      description {
        language
        text
      }
      bookingNotice {
        language
        text
      }
      ticketNotice {
        language
        text
      }
      image {
        fileName
      }
      location {
        name {
          language
          text
        }
        travelAndParkingNotice {
          language
          text
        }
        address {
          street
          streetNumber
          zip
          city
          countryCode
        }
        coordinates {
          lat
          lng
        }
      }
      ticketOrganizerLine {
        language
        text
      }
      status
      enableTicketBooking
      ticketPricePerDay
      instantPaymentOnly
      autoGenerateInvoice
      paymentFlow
      enableStaffBooking
      allowCompanyToBookTickets
      ticketCancellationDeadline
      hasInvitationCodes
      invitationCodeRequired
      suggestedInvitationCode
      bookingSelectDays
      boothBooking
      boothBookingStart
      boothBookingEnd
      startDate
      endDate
      startTime
      endTime
      daySchedules {
        day
        date
        exhibitorStart
        exhibitorEnd
        visitorStart
        visitorEnd
        note {
          language
          text
        }
      }
      exhibitorLectures
      lectureRooms
      lectureRegistrationStartDate
      lectureRegistrationEndDate
      lectureEditEndDate
      lectureDocumentsEndDate
      lecturePresentationEndDate
      lectureRecordingCorrectionEndDate
      actionRegistrationStartDate
      actionRegistrationEndDate
      actionEditEndDate
      deadlineBoothEquipmentPrint
      startBoothEquipmentBooking
      deadlineBoothEquipmentBooking
      startBoothMarketingBooking
      deadlineBoothMarketingBooking
      landingPageUrl
      lecturesNotice {
        language
        text
      }
      floorPlan {
        fileName
        s3Path
      }
      program {
        fileName
        s3Path
      }
      documents {
        fileName
        s3Path
      }
      availableLectureTypes
      industries {
        items {
          industry {
            id
          }
        }
      }
      ticketProduct {
        id
        name {
          language
          text
        }
        price
        image {
          main {
            fileName
          }
        }
      }
      badgePreview {
        fileName
      }
      bookingTicketBulletPoints {
        text {
          language
          text
        }
      }
      ticketTAC {
        language
        text
      }
      ticketPrivacyPolicy {
        language
        text
      }
      boothTAC {
        language
        text
      }
      boothPrivacyPolicy {
        language
        text
      }
      landingPageIntroText {
        language
        text
      }
      landingPageProgramText {
        language
        text
      }
      landingPageProgramSpeakersTitle {
        language
        text
      }
      landingPageProgramSpeakersText {
        language
        text
      }
      landingPageBookingText {
        language
        text
      }
      landingPageBookingPandCText {
        language
        text
      }
      heroBannerStats {
        language
        text
      }
      infoTextExhibitors {
        language
        text
      }
      infoTextVisitors {
        language
        text
      }
      infoTextTravel {
        language
        text
      }
      contactPersons {
        name
        department
        phone
        email
        image {
          fileName
          alt
        }
      }
      highlights {
        mainVideo {
          fileName
          s3Path
        }
        mainText {
          language
          text
        }
        dailys {
          date
          videos {
            fileName
            s3Path
          }
          text {
            language
            text
          }
        }
        images {
          image {
            fileName
            alt
          }
          tags
        }
        videos {
          file {
            fileName
            s3Path
          }
          tags
        }
        pressMedia {
          text {
            language
            text
          }
          media {
            fileName
            s3Path
          }
        }
        exhibitorInfo {
          pricingText {
            language
            text
          }
        }
      }
      carpetColorSelectionEndDate
    }
  }
`;

const UPDATE_EVENT_MUTATION = /* GraphQL */ `
  mutation UpdateEvent($input: UpdateEventInput!) {
    updateEvent(input: $input) {
      id
    }
  }
`;

const CREATE_EVENT_MUTATION = /* GraphQL */ `
  mutation CreateEvent($input: CreateEventInput!) {
    createEvent(input: $input) {
      id
    }
  }
`;

const UPLOAD_IMAGE_MUTATION = /* GraphQL */ `
  mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
    uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
  }
`;